// 208 §6, moved by 216 §2 — A MATTER'S PARTIES, IN THEIR ORDER.
//
// By name, A→Z: an individual by last name then first name, a company by its
// name — [ADVISOR, 208] never by the title (« Mr », « Mevr. ») that leads the
// displayed name (`pname`), which would file every « Mevr. » together. Accents
// and case are ignored (the collator's base strength: « Émile » files with
// « Emile », « de Smet » with « De Smet »); a tie at that strength falls to the
// full comparison, then to whatever the caller breaks it with (the member's
// place in the case, a row's id), so the order never depends on how the engine
// sorts equal keys.
//
// One file, the browser's and the server's (a `.mjs` loads unchanged in both —
// memberRefs.mjs's reason): the collective case's client list
// (memberOrder.ts re-exports it, so its callers did not move), and — ruling 71 —
// every list of a matter's parties drawn or written from here on. Pure.

/**
 * The fields of a party the name order reads: a client, an opponent, a counsel, a contact.
 * @typedef {{ type?: string | null, lastName?: string | null, firstName?: string | null, companyName?: string | null, name?: string | null }} NamedParty
 */

const BASE = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const FULL = new Intl.Collator(undefined, { sensitivity: "variant", numeric: true });

/**
 * The name a party files under: [last, first] — a company's name alone.
 * @param {NamedParty | null | undefined} c
 * @returns {[string, string]}
 */
export function memberNameParts(c) {
  if (!c) return ["", ""];
  const company = (c.type ?? "").toLowerCase() === "company";
  const own = [c.companyName, c.name].map((x) => (x ?? "").trim()).find((x) => x !== "") ?? "";
  if (company) return [own, ""];
  const last = (c.lastName ?? "").trim();
  return [last || own, (c.firstName ?? "").trim()];
}

/**
 * A→Z by last name then first name (a company by its name), accents and case ignored.
 * @param {NamedParty | null | undefined} a
 * @param {NamedParty | null | undefined} b
 * @returns {number}
 */
export function compareMemberNames(a, b) {
  const [al, af] = memberNameParts(a);
  const [bl, bf] = memberNameParts(b);
  return BASE.compare(al, bl) || BASE.compare(af, bf) || FULL.compare(al, bl) || FULL.compare(af, bf);
}

/**
 * 216 §4 [RULED 71, 73] — a list of parties in the one order, a tie falling to the id: what every list of a
 * collective case's people reads (the screen's chips and sublists, the rosters, the modals, the server's party
 * loaders). A regular matter's lists are not passed through it in 216 (ruling 73: they read the order the parties
 * were added, stored by brief 215). Pure; the input is not touched.
 * @template {NamedParty & { id?: number | null }} P
 * @param {readonly P[] | null | undefined} rows
 * @returns {P[]}
 */
export function orderParties(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => compareMemberNames(a, b) || (Number(a?.id ?? 0) - Number(b?.id ?? 0)));
}
